import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import codeExecutionService from '../services/codeExecutionService';

const TryCodingPanel = ({ open, onClose }) => {
  const [code, setCode] = useState('');
  const [imports, setImports] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [executionResult, setExecutionResult] = useState(null);

  // Default Java class structure with main method
  const defaultCode = `public class Main {
    public static void main(String[] args) {
        // Write your code here
        System.out.println("Hello, World!");
    }
}`;

  // Default imports - keep empty by default
  const defaultImports = `// Add your imports here if needed:
// import java.util.*;
// import java.util.Scanner;
// import java.util.Arrays;
// import java.util.ArrayList;
// import java.util.HashMap;
// import java.io.*;
// import java.math.*;

// Uncomment the imports you need:`;

  // Initialize with default values when dialog opens
  React.useEffect(() => {
    if (open) {
      setCode(defaultCode);
      setImports(defaultImports);
      setOutput('');
      setError('');
      setExecutionResult(null);
    }
  }, [open, defaultCode, defaultImports]);

  // Auto-indentation function
  const handleCodeChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const previousValue = code;
    
    // Check if a new line was added
    if (newValue.length > previousValue.length && newValue[cursorPosition - 1] === '\n') {
      const lines = newValue.split('\n');
      const currentLineIndex = newValue.substring(0, cursorPosition).split('\n').length - 1;
      
      // Calculate indentation based on all previous lines
      let braceLevel = 0;
      let indentation = '';
      
      // Count braces from the beginning up to the previous line
      for (let i = 0; i < currentLineIndex; i++) {
        const line = lines[i];
        const openBraces = (line.match(/\{/g) || []).length;
        const closeBraces = (line.match(/\}/g) || []).length;
        braceLevel += openBraces - closeBraces;
      }
      
      // Calculate indentation (4 spaces per level)
      const baseIndentation = '    ';
      for (let i = 0; i < braceLevel; i++) {
        indentation += baseIndentation;
      }
      
      // Insert the indentation
      if (indentation) {
        const newLines = newValue.split('\n');
        newLines[currentLineIndex] = indentation + newLines[currentLineIndex];
        const updatedValue = newLines.join('\n');
        
        // Update the value and set cursor position
        setCode(updatedValue);
        
        // Set cursor position after the indentation
        setTimeout(() => {
          const newCursorPosition = cursorPosition + indentation.length;
          e.target.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
        
        return;
      }
    }
    
    // Check if a closing brace was typed
    if (newValue.length > previousValue.length && newValue[cursorPosition - 1] === '}') {
      const lines = newValue.split('\n');
      const currentLineIndex = newValue.substring(0, cursorPosition).split('\n').length - 1;
      const currentLine = lines[currentLineIndex];
      
      // Calculate expected indentation for this closing brace
      let braceLevel = 0;
      
      // Count braces from the beginning up to the current line (excluding the closing brace we just typed)
      for (let i = 0; i < currentLineIndex; i++) {
        const line = lines[i];
        const openBraces = (line.match(/\{/g) || []).length;
        const closeBraces = (line.match(/\}/g) || []).length;
        braceLevel += openBraces - closeBraces;
      }
      
      // Add braces from the current line (excluding the closing brace we just typed)
      const currentLineBeforeBrace = currentLine.substring(0, currentLine.length - 1);
      const openBraces = (currentLineBeforeBrace.match(/\{/g) || []).length;
      const closeBraces = (currentLineBeforeBrace.match(/\}/g) || []).length;
      braceLevel += openBraces - closeBraces;
      
      // Calculate expected indentation
      const expectedIndentation = '    '.repeat(Math.max(0, braceLevel));
      
      // If the line doesn't match expected indentation, fix it
      const trimmedLine = currentLine.trim();
      if (trimmedLine === '}' && currentLine !== expectedIndentation + '}') {
        const newLines = [...lines];
        newLines[currentLineIndex] = expectedIndentation + '}';
        const updatedValue = newLines.join('\n');
        
        setCode(updatedValue);
        
        // Set cursor position
        setTimeout(() => {
          const newCursorPosition = cursorPosition - (currentLine.length - expectedIndentation.length - 1);
          e.target.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
        
        return;
      }
    }
    
    setCode(newValue);
  };

  // Auto-indentation for imports
  const handleImportsChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const previousValue = imports;
    
    // Check if a new line was added
    if (newValue.length > previousValue.length && newValue[cursorPosition - 1] === '\n') {
      const lines = newValue.split('\n');
      const currentLineIndex = newValue.substring(0, cursorPosition).split('\n').length - 1;
      const previousLine = lines[currentLineIndex - 1] || '';
      
      // Check if the previous line is a comment or import statement
      if (previousLine.trim().startsWith('//') || previousLine.trim().startsWith('import')) {
        const indentation = '    '; // 4 spaces for imports
        const newLines = newValue.split('\n');
        newLines[currentLineIndex] = indentation + newLines[currentLineIndex];
        const updatedValue = newLines.join('\n');
        
        setImports(updatedValue);
        
        // Set cursor position after the indentation
        setTimeout(() => {
          const newCursorPosition = cursorPosition + indentation.length;
          e.target.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
        
        return;
      }
    }
    
    setImports(newValue);
  };

  const handleRunCode = async () => {
    if (!code.trim()) {
      setError('Please enter some code to run');
      return;
    }

    setLoading(true);
    setError('');
    setOutput('Executing code...');
    setExecutionResult(null);

    try {
      // Combine imports and code
      const fullCode = imports.trim() + '\n\n' + code.trim();
      
      const response = await codeExecutionService.executeCode(fullCode);
      
      if (response.result === 'SUCCESS') {
        const result = response.data;
        setExecutionResult(result);
        
        // Format the output
        let outputText = '';
        
        if (result.stdout) {
          outputText += `Output:\n${result.stdout}\n\n`;
        }
        
        if (result.stderr) {
          outputText += `Errors:\n${result.stderr}\n\n`;
        }
        
        if (result.compileOutput) {
          outputText += `Compilation:\n${result.compileOutput}\n\n`;
        }
        
        if (result.message) {
          outputText += `Message: ${result.message}\n\n`;
        }
        
        if (result.executionTime) {
          outputText += `Execution Time: ${result.executionTime}ms\n`;
        }
        
        if (result.memory) {
          outputText += `Memory Used: ${result.memory}KB`;
        }
        
        setOutput(outputText || 'Code executed successfully (no output)');
      } else {
        setError(response.message || 'Code execution failed');
        setOutput('');
      }
    } catch (error) {
      setError('Code execution failed: ' + (error.message || error));
      setOutput('');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setImports('');
    setOutput('');
    setError('');
    setLoading(false);
    setExecutionResult(null);
    onClose();
  };

  const handleClear = () => {
    setCode(defaultCode);
    setImports(defaultImports);
    setOutput('');
    setError('');
    setExecutionResult(null);
  };

  const handleReset = () => {
    setCode(defaultCode);
    setImports(defaultImports);
  };

  const getStatusColor = (status) => {
    if (status?.toLowerCase().includes('accepted')) return 'success';
    if (status?.toLowerCase().includes('error')) return 'error';
    if (status?.toLowerCase().includes('wrong')) return 'warning';
    return 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: { xs: '100%', md: '90vh' },
          maxHeight: 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          margin: { xs: 'calc(12px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) calc(12px + env(safe-area-inset-bottom, 0px)) calc(12px + env(safe-area-inset-left, 0px))', md: 3 },
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'white',
        pt: { xs: 'calc(12px + env(safe-area-inset-top, 0px))', sm: 2 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CodeIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              Java Code Playground
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, display: { xs: 'none', sm: 'block' } }}>
              Write, test, and run Java code in real-time
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, height: '100%' }}>
          {/* Left Panel - Code Input */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Code Editor
            </Typography>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Imports (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={imports}
                onChange={handleImportsChange}
                placeholder="Add your imports here..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 250, md: 'auto' } }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Java Code
              </Typography>
              <TextField
                fullWidth
                multiline
                value={code}
                onChange={handleCodeChange}
                placeholder="Write your Java code here..."
                variant="outlined"
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    height: '100% !important',
                    overflow: 'auto',
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleRunCode}
                disabled={loading}
                sx={{
                  flex: { xs: '1 1 100%', sm: 1 },
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
                  }
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Run Code'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ px: 2, fontWeight: 600, borderRadius: 2 }}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ px: 2, fontWeight: 600, borderRadius: 2 }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Right Panel - Output */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Output
            </Typography>
            
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                '& pre': {
                  backgroundColor: 'transparent',
                  padding: 0,
                  margin: 0,
                  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }
              }}
            >
              {output ? (
                <pre style={{ margin: 0 }}>{output}</pre>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Run your code to see the output here...
                </Typography>
              )}
            </Paper>

            {executionResult && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {executionResult.executionTime && (
                  <Chip
                    label={`Time: ${executionResult.executionTime}ms`}
                    size="small"
                    color="info"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {executionResult.memory && (
                  <Chip
                    label={`Memory: ${executionResult.memory}KB`}
                    size="small"
                    color="info"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {executionResult.status && (
                  <Chip
                    label={executionResult.status}
                    size="small"
                    color={getStatusColor(executionResult.status)}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TryCodingPanel; 